import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success(`Welcome to TradePilot, ${name}! You're funded with $100,000 in paper money.`);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <img src="/icons/icon-192.png" alt="TradePilot" className="w-10 h-10 rounded-lg shadow-sm ring-1 ring-brand/10" />
          <span className="font-display font-semibold text-xl text-brand-ink">
            Trade<span className="text-brand">Pilot</span>
          </span>
        </Link>
        <h1 className="font-display text-4xl font-medium tracking-tight text-center">Create your account</h1>
        <p className="text-center text-gray-500 mt-2 text-sm">Get $100,000 in paper money to trade.</p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="register-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Display name</Label>
            <Input
              data-testid="register-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Trader"
              className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Email</Label>
            <Input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Password</Label>
            <Input
              data-testid="register-password"
              type="password"
              minLength={6}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2"
            />
          </div>
          {err && (
            <p data-testid="register-error" className="text-sm text-loss">
              {err}
            </p>
          )}
          <Button
            data-testid="register-submit"
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-brand-gradient text-white hover:opacity-90 font-medium shadow-sm"
          >
            {busy ? "Creating account…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-center text-gray-500 mt-8">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-deep font-medium hover:underline" data-testid="register-to-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
